import React from 'react';
import {Box, Stack} from '@mui/material';
import {SwitchButton} from '@lightricks/react-design-system';
import {
  CaptionsStylePreset,
  Color,
  DropShadow,
} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import {
  SectionLabel,
  StyledSlider,
  ColorPicker,
  LabeledSlider,
  DividerLine,
  StyledToggleContainer,
  KeyboardNavigableInput,
  designSystemToken,
  Icon,
} from './SharedConstants';

interface StylingSectionProps {
  updateStyling: (
    updates: Partial<CaptionsStylePreset['captionsStyle']['styling']>
  ) => void;
  editedStyle: CaptionsStylePreset;
}

// Default values and value ranges
const DEFAULT_HEX_COLOR = '#000000';
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_STROKE_COLOR: Color = {hex: DEFAULT_HEX_COLOR, opacity: 1};
const DEFAULT_BACKGROUND_COLOR: Color = {hex: DEFAULT_HEX_COLOR, opacity: 0.7};
const DEFAULT_CORNER_RADIUS = 8;
const DEFAULT_VERTICAL_PADDING = 0.4;
const DEFAULT_HORIZONTAL_PADDING = 0.3;

const MIN_CORNER_RADIUS = 2;
const MAX_CORNER_RADIUS = 1000;
const MIN_STROKE_WIDTH = 0;
const MAX_STROKE_WIDTH = 250;
const MIN_OPACITY_PERCENT = 0;
const MAX_OPACITY_PERCENT = 100;
const MIN_PADDING_PERCENT = 0;
const MAX_PADDING_PERCENT = 1000;

const MIN_BLUR = 0;
const MAX_BLUR = 200;
const MIN_OFFSET = -100;
const MAX_OFFSET = 100;
const DEFAULT_BLUR = 0;
const DEFAULT_VERTICAL_OFFSET = 10;
const DEFAULT_HORIZONTAL_OFFSET = 10;

const DEFAULT_DROP_SHADOW: DropShadow = {
  color: {hex: DEFAULT_HEX_COLOR, opacity: 1},
  offsetX: DEFAULT_HORIZONTAL_OFFSET,
  offsetY: DEFAULT_VERTICAL_OFFSET,
  blur: DEFAULT_BLUR,
};

// Utility methods

const extractNumericValue = (inputValue: string): string => {
  return inputValue.replace(/[^0-9]/g, '');
};

const clampStrokeWidth = (value: string): number => {
  return Math.max(
    MIN_STROKE_WIDTH,
    Math.min(MAX_STROKE_WIDTH, parseInt(value, 10) || 0)
  );
};

const extractPercentageValue = (inputValue: string): string => {
  return inputValue.replace('%', '').replace(/[^0-9]/g, '');
};

const clampOpacityPercentage = (value: string): number => {
  return (
    Math.max(
      MIN_OPACITY_PERCENT,
      Math.min(MAX_OPACITY_PERCENT, parseInt(value, 10) || 0)
    ) / 100
  );
};

const opacityToPercentageString = (opacity: number): string => {
  return `${Math.round(opacity * 100)}%`;
};

function StrokeSection({updateStyling, editedStyle}: StylingSectionProps) {
  const handleStrokeColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateStyling({
      textStroke: {
        width:
          editedStyle.captionsStyle.styling.textStroke?.width ??
          DEFAULT_STROKE_WIDTH,
        color: {
          hex: e.target.value,
          opacity:
            editedStyle.captionsStyle.styling.textStroke?.color.opacity ??
            DEFAULT_STROKE_COLOR.opacity,
        },
      },
    });
  };

  const handleStrokeWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = extractNumericValue(e.target.value);
    const width = clampStrokeWidth(value);
    updateStyling({
      textStroke: {
        color:
          editedStyle.captionsStyle.styling.textStroke?.color ||
          DEFAULT_STROKE_COLOR,
        width,
      },
    });
  };

  return (
    <Box>
      <StyledToggleContainer>
        <SectionLabel size="md" sx={{mb: 0}}>
          {translate('Stroke')}
        </SectionLabel>
        <SwitchButton
          checkedTrackColor={designSystemToken('semantic.fg.primary')}
          checked={!!editedStyle.captionsStyle.styling.textStroke}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateStyling({
              textStroke: e.target.checked
                ? {width: DEFAULT_STROKE_WIDTH, color: DEFAULT_STROKE_COLOR}
                : null,
            })
          }
        />
      </StyledToggleContainer>
      {editedStyle.captionsStyle.styling.textStroke && (
        <Stack direction="row" gap="16px" mt="16px">
          <ColorPicker
            label="Color"
            value={
              editedStyle.captionsStyle.styling.textStroke?.color?.hex ||
              DEFAULT_HEX_COLOR
            }
            onChange={handleStrokeColorChange}
            sx={{flex: 1}}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: designSystemToken('semantic.bg.primary'),
              borderRadius: '8px',
              border: `1px solid ${designSystemToken(
                'semantic.bg.neutral-secondary'
              )}`,
              padding: '8px 12px',
              width: '65px',
              height: '40px',
              boxSizing: 'border-box',
            }}
          >
            <Icon name="Features-Stroke" size="medium" appearance="neutral" />
            <KeyboardNavigableInput
              value={editedStyle.captionsStyle.styling.textStroke?.width || 0}
              onChange={handleStrokeWidthChange}
              min={MIN_STROKE_WIDTH}
              max={MAX_STROKE_WIDTH}
              step={1}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function DropShadowSection({updateStyling, editedStyle}: StylingSectionProps) {
  const {dropShadow} = editedStyle.captionsStyle.styling;

  const handleDropShadowColorChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!dropShadow) return;
    updateStyling({
      dropShadow: {
        ...dropShadow,
        color: {
          hex: e.target.value,
          opacity: dropShadow.color?.opacity ?? 1,
        },
      },
    });
  };

  return (
    <Box>
      <StyledToggleContainer>
        <SectionLabel size="md" sx={{mb: 0}}>
          {translate('Drop Shadow')}
        </SectionLabel>
        <SwitchButton
          checkedTrackColor={designSystemToken('semantic.fg.primary')}
          checked={!!editedStyle.captionsStyle.styling.dropShadow}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateStyling({
              dropShadow: e.target.checked ? DEFAULT_DROP_SHADOW : null,
            })
          }
        />
      </StyledToggleContainer>
      {editedStyle.captionsStyle.styling.dropShadow && (
        <Stack gap="16px" mt="16px">
          <ColorPicker
            label="Color"
            value={
              editedStyle.captionsStyle.styling.dropShadow?.color?.hex ||
              DEFAULT_HEX_COLOR
            }
            onChange={handleDropShadowColorChange}
            sx={{flex: 1}}
          />

          <Box>
            <Stack direction="row" justifyContent="space-between" mb="8px">
              <SectionLabel size="md" sx={{mb: 0}}>
                {translate('Blur')}
              </SectionLabel>
              <SectionLabel size="md" sx={{mb: 0}}>
                {dropShadow?.blur || DEFAULT_BLUR}px
              </SectionLabel>
            </Stack>
            <StyledSlider
              value={dropShadow?.blur ?? DEFAULT_BLUR}
              min={MIN_BLUR}
              max={MAX_BLUR}
              onChange={(_, value) => {
                if (!dropShadow) return;
                updateStyling({
                  dropShadow: {
                    ...dropShadow,
                    blur: value as number,
                  },
                });
              }}
            />
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function BackgroundSection({updateStyling, editedStyle}: StylingSectionProps) {
  const {background} = editedStyle.captionsStyle.styling;

  const handleBackgroundColorChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateStyling({
      background: {
        ...background,
        color: {
          hex: e.target.value,
          opacity:
            background?.color?.opacity ?? DEFAULT_BACKGROUND_COLOR.opacity,
        },
      },
    });
  };

  const handleBackgroundOpacityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = extractPercentageValue(e.target.value);
    const opacity = clampOpacityPercentage(value);
    updateStyling({
      background: {
        ...background,
        color: {
          hex: background?.color?.hex || DEFAULT_HEX_COLOR,
          opacity,
        },
      },
    });
  };

  return (
    <Box>
      <StyledToggleContainer>
        <SectionLabel size="md" sx={{mb: 0}}>
          {translate('Background')}
        </SectionLabel>
        <SwitchButton
          checkedTrackColor={designSystemToken('semantic.fg.primary')}
          checked={!!background}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateStyling({
              background: e.target.checked
                ? {
                    color: DEFAULT_BACKGROUND_COLOR,
                    cornerRadius: DEFAULT_CORNER_RADIUS,
                    verticalPadding: DEFAULT_VERTICAL_PADDING,
                    horizontalPadding: DEFAULT_HORIZONTAL_PADDING,
                  }
                : null,
            })
          }
        />
      </StyledToggleContainer>
      {background && (
        <Stack gap="16px" mt="16px">
          <Stack direction="row" gap="16px">
            <ColorPicker
              label="Color"
              value={background.color?.hex || DEFAULT_HEX_COLOR}
              onChange={handleBackgroundColorChange}
              sx={{flex: 1}}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '0px 12px 0px 3px',
                width: '82px',
                height: '40px',
                boxSizing: 'border-box',
                backgroundColor: designSystemToken('semantic.bg.primary'),
                borderRadius: '8px',
                border: `1px solid ${designSystemToken(
                  'semantic.bg.neutral-secondary'
                )}`,
              }}
            >
              <Icon
                name="Background-Settings"
                size="large"
                appearance="neutral"
              />
              <KeyboardNavigableInput
                value={opacityToPercentageString(
                  background.color?.opacity ?? DEFAULT_BACKGROUND_COLOR.opacity
                )}
                onChange={handleBackgroundOpacityChange}
                min={MIN_OPACITY_PERCENT}
                max={MAX_OPACITY_PERCENT}
                step={5}
                suffix="%"
              />
            </Box>
          </Stack>

          <LabeledSlider
            label={translate('Corner Radius')}
            value={background.cornerRadius ?? DEFAULT_CORNER_RADIUS}
            displayValue={`${
              background.cornerRadius ?? DEFAULT_CORNER_RADIUS
            }px`}
            min={MIN_CORNER_RADIUS}
            max={MAX_CORNER_RADIUS}
            step={1}
            onChange={(value) => {
              updateStyling({
                background: {
                  ...background,
                  cornerRadius: value,
                },
              });
            }}
            iconName="Features-Corners"
            iconColor={designSystemToken('semantic.fg.secondary')}
          />
        </Stack>
      )}
    </Box>
  );
}

function StylingSection({updateStyling, editedStyle}: StylingSectionProps) {
  return (
    <Box>
      <Stack gap="16px">
        <BackgroundSection
          updateStyling={updateStyling}
          editedStyle={editedStyle}
        />
        <DividerLine />
        <StrokeSection
          updateStyling={updateStyling}
          editedStyle={editedStyle}
        />
        <DividerLine />
        <DropShadowSection
          updateStyling={updateStyling}
          editedStyle={editedStyle}
        />
      </Stack>
    </Box>
  );
}

export default StylingSection;
